// src/Pages/Friends.js
import React, { useState, useEffect } from 'react';
import '../Styles/friends.css';
import { Link } from 'react-router-dom';
import SERVER from '../server.js'; 

const unactiveHomeIcon = `${SERVER}/img/home-unactive.png`;
const unactiveWishlistIcon = `${SERVER}/img/wishlist-unactive.png`;
const unactiveCartIcon = `${SERVER}/img/cart-unactive.png`;
const activeFriendsIcon = `${SERVER}/img/friends-active.png`;

const avatar = `${SERVER}/img/avatar.png`;

function Friends() {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [wishlistItemCount, setWishlistItemCount] = useState(0);

  useEffect(() => {
    const initUser = async () => {
      if (window.Telegram && window.Telegram.WebApp) {
        const tg = window.Telegram.WebApp;
        const userData = tg.initDataUnsafe?.user;
        
        if (userData) {
          const userInfo = {
            id: userData.id,
            firstName: userData.first_name,
            lastName: userData.last_name,
            username: userData.username
          };
          
          setUser(userInfo);
          await fetchUserData(userData.id);
        } else {
          setError('Telegram user data not available. Please open this app through Telegram.');
          setLoading(false);
        }
      } else {
        setError('This app can only be used through Telegram. Please open it via Telegram Web App.');
        setLoading(false);
      }
    };

    initUser();
  }, []);

  const fetchUserData = async (userId) => {
    try {
      // Fetch referrals
      const referralsResponse = await fetch(`${SERVER}/users/${userId}/referrals`);
      if (referralsResponse.ok) {
        const data = await referralsResponse.json();
        setReferrals(data.referrals || []);
      }

      // Fetch cart count
      const cartResponse = await fetch(`${SERVER}/users/${userId}/cart`);
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        setCartItemCount(cartData.items ? cartData.items.length : 0);
      }

      // Fetch wishlist count
      const wishlistResponse = await fetch(`${SERVER}/users/${userId}/wishlist`);
      if (wishlistResponse.ok) {
        const wishlistData = await wishlistResponse.json();
        setWishlistItemCount(Array.isArray(wishlistData) ? wishlistData.length : 0);
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleInviteClick = () => {
    const inviteLink = `https://t.me/Vercetti_T_bot?start=ref_${user.id}`;
    
    navigator.clipboard.writeText(inviteLink)
      .then(() => {
        setShowCopiedMessage(true);
        setTimeout(() => setShowCopiedMessage(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
        const textArea = document.createElement('textarea');
        textArea.value = inviteLink;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          setShowCopiedMessage(true);
          setTimeout(() => setShowCopiedMessage(false), 3000);
        } catch (err) {
          console.error('Fallback copy failed: ', err);
        }
        document.body.removeChild(textArea);
      });
  };

  const totalEarnings = referrals.length * 1; // $1 per referral

  if (loading) {
    return (
      <div className="friends-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <div>Loading your referrals...</div>
          <small>Calculating your earnings</small>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="friends-container">
        <div className="error">
          <div className="error-icon">🤝</div>
          <strong>Unable to load referrals</strong>
          <br />
          {error}
          <br />
          <div className="error-actions">
            <button onClick={() => window.location.reload()} className="retry-btn">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="friends-container">
      <header className="friends-header">
        <div className="header-content">
          <div className="deal-e">🤝</div>
          <div className="header-text">
            <h2>AFFILIATE PROGRAM</h2>
            <p className="friends-subtitle">Invite friends and earn rewards</p>
          </div>
        </div>
      </header>

      {/* Earnings Overview */}
      <div className="earnings-overview">
        <div className="earnings-card">
          <div className="earnings-item">
            <span className="earnings-label">Total Earnings</span>
            <span className="earnings-value">${totalEarnings.toFixed(2)}</span>
          </div>
          <div className="earnings-item">
            <span className="earnings-label">Friends Invited</span>
            <span className="earnings-value">{referrals.length}</span>
          </div>
          <div className="earnings-item">
            <span className="earnings-label">Per Referral</span>
            <span className="earnings-value">$1.00</span>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="how-it-works">
        <h3>How it works</h3>
        <div className="steps">
          <div className="step">
            <div className="step-icon">📤</div>
            <div className="step-content">
              <h4>Share your link</h4>
              <p>Send your referral link to friends</p>
            </div>
          </div>
          <div className="step">
            <div className="step-icon">👥</div>
            <div className="step-content">
              <h4>Friend joins</h4>
              <p>They sign up using your link</p>
            </div>
          </div>
          <div className="step">
            <div className="step-icon">💰</div>
            <div className="step-content">
              <h4>You earn money</h4>
              <p>Get $1 for each successful referral</p>
            </div>
          </div>
        </div>
      </div>

      {/* Friends List */}
      <div className="friends-section">
        <h3>Your Referrals ({referrals.length})</h3>
        <div className="friends-list">
          {referrals.length > 0 ? (
            referrals.map((referral, index) => (
              <div key={index} className="friend-card">
                <div className="friend-info">
                  <img src={avatar} alt={referral.username || `User ${referral.user_id}`} className="friend-avatar" />
                  <div className="friend-details">
                    <span className="friend-name">
                      {referral.first_name || referral.username || `User ${referral.user_id}`}
                    </span>
                    <span className="friend-date">
                      Joined {referral.joined_date ? new Date(referral.joined_date).toLocaleDateString() : 'Recently'}
                    </span>
                  </div>
                </div>
                <div className="friend-earning">
                  <span className="earning-amount">+$1.00</span>
                  <span className="earning-status">✅</span>
                </div>
              </div>
            ))
          ) : (
            <div className="no-referrals">
              <div className="no-referrals-icon">👥</div>
              <h4>No referrals yet</h4>
              <p>Start inviting friends and earn $1 for each successful referral!</p>
            </div>
          )}
        </div>
      </div>

      {/* Invite Section */}
      <div className="invite-section">
        <button className="invite-btn" onClick={handleInviteClick}>
          <span className="invite-icon">📤</span>
          <span>Invite Friends & Earn Money</span>
        </button>
        {showCopiedMessage && (
          <div className="copied-message">
            <span className="copied-icon">✅</span>
            Link copied to clipboard!
          </div>
        )}
      </div>

      <nav className="bottom-nav">
        <Link to="/" className="nav-btn">
          <img src={unactiveHomeIcon} alt="Home" className="nav-icon" />
          <span>Home</span>
        </Link>
        <Link to="/Wishlist" className="nav-btn">
          <img src={unactiveWishlistIcon} alt="Wishlist" className="nav-icon" />
          <span>Wishes</span>
          {wishlistItemCount > 0 && (
            <span className="nav-badge">{wishlistItemCount}</span>
          )}
        </Link>
        <Link to="/Cart" className="nav-btn">
          <img src={unactiveCartIcon} alt="Cart" className="nav-icon" />
          <span>Cart</span>
          {cartItemCount > 0 && (
            <span className="nav-badge">{cartItemCount}</span>
          )}
        </Link>
        <Link to="/Friends" className="nav-btn active">
          <img src={activeFriendsIcon} alt="Friends" className="nav-icon" />
          <span>Friends</span>
        </Link>
      </nav>
    </div>
  );
}

export default Friends;